import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import PublicLayout from '../components/public/PublicLayout';
import SearchableSelect from '../components/SearchableSelect';
import LoginGate from '../components/LoginGate';
import { useAuth } from '../App';
import { Search, MapPin, GraduationCap, Award, Globe2, BookOpen, ChevronRight, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LandingPage = () => {
  const [degreeType, setDegreeType] = useState('');
  const [country, setCountry] = useState('');
  const [subject, setSubject] = useState('');
  const [countries, setCountries] = useState([]);
  const [degreeTypes, setDegreeTypes] = useState([]);
  const [stats, setStats] = useState({ universities: 0, campuses: 0, programs: 0, degree_types: 0 });
  const [showGate, setShowGate] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const load = async () => {
      try {
        const [c, d, st] = await Promise.all([
          axios.get(`${API}/public/master-data?type=country`).catch(() => ({ data: [] })),
          axios.get(`${API}/public/master-data?type=degree_type`).catch(() => ({ data: [] })),
          axios.get(`${API}/public/hierarchy-stats`).catch(() => ({ data: {} })),
        ]);
        // Defensive: collapse case-insensitive duplicates so the dropdown never
        // shows e.g. "MSc / MSc" even if old data still has parent-scoped rows.
        const uniqByName = (arr) => {
          const seen = new Set();
          const out = [];
          for (const x of Array.isArray(arr) ? arr : []) {
            const key = String(x?.name || '').trim().toLowerCase();
            if (!key || seen.has(key)) continue;
            seen.add(key);
            out.push(x);
          }
          return out;
        };
        setCountries(uniqByName(c.data));
        setDegreeTypes(uniqByName(d.data));
        const s = st.data || {};
        setStats({
          universities: Number(s.universities) || 0,
          campuses: Number(s.campuses) || 0,
          programs: Number(s.programs) || 0,
          degree_types: Number(s.degree_types) || 0,
        });
      } catch (e) { /* silent */ }
    };
    load();
  }, []);

  const onFind = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (degreeType) params.set('degree', degreeType);
    if (country) params.set('country', country);
    if (subject) params.set('subject', subject);
    const target = `/courses?${params.toString()}`;
    if (!user) {
      // Guest — show login gate; deep-link the result page so they land here after auth
      sessionStorage.setItem('ebn_next_after_login', target);
      setShowGate(true);
      return;
    }
    window.location.href = target;
  };

  return (
    <PublicLayout>
      {showGate && (
        <LoginGate
          title="Sign in to view your matches"
          subtitle="EduBridge personalises every course recommendation to your grades, budget, and visa profile. Sign in or create a free account to see your results."
          returnTo={`/courses?${new URLSearchParams({...(degreeType?{degree:degreeType}:{}),...(country?{country}:{}),...(subject?{subject}:{})}).toString()}`}
          onClose={() => setShowGate(false)}
        />
      )}
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'url("https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=1920&q=80")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1A2A4F]/40 via-[#1A2A4F]/10 to-transparent" />

        <div className="relative max-w-7xl mx-auto px-4 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div className="text-white drop-shadow-lg">
            <h1 className="text-4xl sm:text-5xl font-bold leading-tight tracking-tight">
              We maximize your chance of getting <em className="italic text-[#F5A623] not-italic-but-styled font-bold">admitted</em> to your chosen universities
            </h1>
            <p className="mt-5 text-base sm:text-lg max-w-xl opacity-95">
              The smart alternative to applying direct for a degree abroad and the easy way to find programs that match your eligibility and aspirations.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              {/* Hero CTAs intentionally removed — students are funneled via the
                  filter card on the right and the global Register/Sign-In header. */}
            </div>
          </div>

          {/* Right side filter card */}
          <form onSubmit={onFind} className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 max-w-md ml-auto w-full" data-testid="hero-find-form">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#1A2A4F] mb-1.5">Select a degree type</label>
                <SearchableSelect
                  value={degreeType}
                  onChange={setDegreeType}
                  options={degreeTypes.map((d) => ({ value: d.name, label: d.name }))}
                  placeholder="Select a degree type"
                  testid="hero-degree-select"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A2A4F] mb-1.5">Select for a location</label>
                <SearchableSelect
                  value={country}
                  onChange={setCountry}
                  options={countries.map((c) => ({ value: c.name, label: c.name }))}
                  placeholder="Select for a location"
                  testid="hero-country-select"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A2A4F] mb-1.5">Select for a subject</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="e.g. Computer Science"
                  className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#F5A623]/20 focus:border-[#F5A623] outline-none"
                  data-testid="hero-subject-input"
                />
              </div>
              <button
                type="submit"
                className="w-full bg-[#F5A623] text-white font-semibold py-3 rounded-md hover:brightness-95 transition flex items-center justify-center gap-2"
                data-testid="hero-find-submit-btn"
              >
                <Search className="w-4 h-4" /> Find Program
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="bg-[#1A2A4F] text-white" data-testid="stats-strip">
        <div className="max-w-7xl mx-auto px-4 py-10 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          <Stat icon={<GraduationCap className="w-7 h-7" />} value={`${stats.universities}`} label="Universities" testid="stat-universities" />
          <Stat icon={<MapPin className="w-7 h-7" />} value={`${stats.campuses}`} label="Campuses" testid="stat-campuses" />
          <Stat icon={<BookOpen className="w-7 h-7" />} value={`${stats.programs}`} label="Programs" testid="stat-programs" />
          <Stat icon={<Award className="w-7 h-7" />} value={`${stats.degree_types}`} label="Degree Types" testid="stat-degree-types" />
        </div>
      </section>

      {/* WHY US */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <p className="text-[#F5A623] font-semibold tracking-widest text-xs uppercase">Why Edu Bridge Network</p>
          <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-[#1A2A4F]">Your study-abroad journey, simplified.</h2>
          <p className="mt-3 text-gray-600 max-w-2xl mx-auto">From first enquiry to enrolment — we handle universities, course matching, documents, visa and beyond.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { icon: <GraduationCap className="w-6 h-6" />, t: 'Vetted Universities', d: 'Hand-picked partner institutions across 10+ countries.' },
            { icon: <BookOpen className="w-6 h-6" />, t: 'Course Matchmaker', d: 'Filter by degree, location, subject — find the right program in seconds.' },
            { icon: <Award className="w-6 h-6" />, t: 'Scholarships', d: 'Live database of scholarships you actually qualify for.' },
            { icon: <CheckCircle2 className="w-6 h-6" />, t: 'Document Vetting', d: 'AI-assisted document checking so your application never gets rejected on technicalities.' },
            { icon: <Globe2 className="w-6 h-6" />, t: 'Visa Guidance', d: 'Step-by-step CAS, visa, and arrival support from our admissions team.' },
            { icon: <MapPin className="w-6 h-6" />, t: 'Onboard Help', d: 'Accommodation, airport pickup, and on-campus orientation — wrapped in one service.' },
          ].map((f) => (
            <div key={f.t} className="group border border-gray-100 rounded-xl p-6 hover:border-[#F5A623] hover:shadow-lg transition" data-testid={`feature-${f.t.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="w-12 h-12 rounded-full bg-[#F5A623]/10 text-[#F5A623] flex items-center justify-center group-hover:bg-[#F5A623] group-hover:text-white transition">
                {f.icon}
              </div>
              <h3 className="mt-4 font-bold text-lg text-[#1A2A4F]">{f.t}</h3>
              <p className="mt-2 text-sm text-gray-600 leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA BAND */}
      <section className="bg-[#F5A623]">
        <div className="max-w-7xl mx-auto px-4 py-12 grid grid-cols-1 md:grid-cols-2 items-center gap-6">
          <div>
            <h3 className="text-2xl sm:text-3xl font-bold text-[#1A2A4F]">Ready to start your application?</h3>
            <p className="mt-2 text-[#1A2A4F]/90">Create a free account and our counsellors will match you to programs the same day.</p>
          </div>
          <div className="flex flex-wrap gap-3 md:justify-end">
            <Link to="/register" className="bg-[#1A2A4F] text-white font-semibold px-7 py-3 rounded-md hover:opacity-90 transition" data-testid="cta-band-register">Sign Up Free</Link>
            <Link to="/contact" className="bg-white text-[#1A2A4F] font-semibold px-7 py-3 rounded-md hover:bg-gray-50 transition" data-testid="cta-band-contact">Talk to a Counsellor</Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

const Stat = ({ icon, value, label, testid }) => (
  <div className="flex flex-col items-center gap-2" data-testid={testid}>
    <div className="w-12 h-12 rounded-full bg-[#F5A623]/15 text-[#F5A623] flex items-center justify-center">{icon}</div>
    <div className="text-3xl font-bold" data-testid={testid ? `${testid}-value` : undefined}>{value}</div>
    <div className="text-sm opacity-80">{label}</div>
  </div>
);

export default LandingPage;
